<template>
  <div
    v-if="isOpen"
    class="keyboard-shortcuts"
    @keydown.esc.stop="close"
  >
    <div class="keyboard-shortcuts__overlay" @click="close"></div>
    <div class="keyboard-shortcuts__panel">
      <div class="keyboard-shortcuts__header">
        <h2 class="keyboard-shortcuts__title">Keyboard Shortcuts</h2>
        <button class="keyboard-shortcuts__close" @click="close">
          <icon-close></icon-close>
        </button>
      </div>
      <div class="keyboard-shortcuts__content">
        <div class="shortcuts-section">
          <h3 class="shortcuts-section__title">General</h3>
          <div class="shortcut-item">
            <span class="shortcut-keys"><kbd>{{ modKey }}</kbd> + <kbd>S</kbd></span>
            <span class="shortcut-desc">Save file</span>
          </div>
          <div class="shortcut-item">
            <span class="shortcut-keys"><kbd>{{ modKey }}</kbd> + <kbd>F</kbd></span>
            <span class="shortcut-desc">Find in file</span>
          </div>
          <div class="shortcut-item">
            <span class="shortcut-keys"><kbd>{{ modKey }}</kbd> + <kbd>Shift</kbd> + <kbd>F</kbd></span>
            <span class="shortcut-desc">Search in all files</span>
          </div>
          <div class="shortcut-item">
            <span class="shortcut-keys"><kbd>{{ modKey }}</kbd> + <kbd>Alt</kbd> + <kbd>F</kbd></span>
            <span class="shortcut-desc">Find and replace</span>
          </div>
        </div>

        <div class="shortcuts-section">
          <h3 class="shortcuts-section__title">Formatting</h3>
          <div class="shortcut-item">
            <span class="shortcut-keys"><kbd>{{ modKey }}</kbd> + <kbd>Shift</kbd> + <kbd>B</kbd></span>
            <span class="shortcut-desc">Bold</span>
          </div>
          <div class="shortcut-item">
            <span class="shortcut-keys"><kbd>{{ modKey }}</kbd> + <kbd>Shift</kbd> + <kbd>I</kbd></span>
            <span class="shortcut-desc">Italic</span>
          </div>
          <div class="shortcut-item">
            <span class="shortcut-keys"><kbd>{{ modKey }}</kbd> + <kbd>Shift</kbd> + <kbd>S</kbd></span>
            <span class="shortcut-desc">Strikethrough</span>
          </div>
          <div class="shortcut-item">
            <span class="shortcut-keys"><kbd>{{ modKey }}</kbd> + <kbd>Shift</kbd> + <kbd>K</kbd></span>
            <span class="shortcut-desc">Inline code</span>
          </div>
          <div class="shortcut-item">
            <span class="shortcut-keys"><kbd>{{ modKey }}</kbd> + <kbd>Shift</kbd> + <kbd>H</kbd></span>
            <span class="shortcut-desc">Heading</span>
          </div>
        </div>

        <div class="shortcuts-section">
          <h3 class="shortcuts-section__title">Insert</h3>
          <div class="shortcut-item">
            <span class="shortcut-keys"><kbd>{{ modKey }}</kbd> + <kbd>Shift</kbd> + <kbd>L</kbd></span>
            <span class="shortcut-desc">Link</span>
          </div>
          <div class="shortcut-item">
            <span class="shortcut-keys"><kbd>{{ modKey }}</kbd> + <kbd>Shift</kbd> + <kbd>G</kbd></span>
            <span class="shortcut-desc">Image</span>
          </div>
          <div class="shortcut-item">
            <span class="shortcut-keys"><kbd>{{ modKey }}</kbd> + <kbd>Shift</kbd> + <kbd>T</kbd></span>
            <span class="shortcut-desc">Table</span>
          </div>
          <div class="shortcut-item">
            <span class="shortcut-keys"><kbd>{{ modKey }}</kbd> + <kbd>Shift</kbd> + <kbd>R</kbd></span>
            <span class="shortcut-desc">Horizontal rule</span>
          </div>
        </div>

        <div class="shortcuts-section">
          <h3 class="shortcuts-section__title">Lists</h3>
          <div class="shortcut-item">
            <span class="shortcut-keys"><kbd>{{ modKey }}</kbd> + <kbd>Shift</kbd> + <kbd>U</kbd></span>
            <span class="shortcut-desc">Unordered list</span>
          </div>
          <div class="shortcut-item">
            <span class="shortcut-keys"><kbd>{{ modKey }}</kbd> + <kbd>Shift</kbd> + <kbd>O</kbd></span>
            <span class="shortcut-desc">Ordered list</span>
          </div>
          <div class="shortcut-item">
            <span class="shortcut-keys"><kbd>{{ modKey }}</kbd> + <kbd>Shift</kbd> + <kbd>C</kbd></span>
            <span class="shortcut-desc">Checklist</span>
          </div>
          <div class="shortcut-item">
            <span class="shortcut-keys"><kbd>{{ modKey }}</kbd> + <kbd>Shift</kbd> + <kbd>Q</kbd></span>
            <span class="shortcut-desc">Blockquote</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KeyboardShortcuts',
  data() {
    return {
      isOpen: false,
    };
  },
  computed: {
    modKey() {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      return isMac ? '⌘' : 'Ctrl';
    },
  },
  mounted() {
    window.addEventListener('keydown', this.handleGlobalKeydown);
    window.addEventListener('open-keyboard-shortcuts', this.open);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleGlobalKeydown);
    window.removeEventListener('open-keyboard-shortcuts', this.open);
  },
  methods: {
    handleGlobalKeydown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        this.open();
      }
    },
    open() {
      this.isOpen = true;
      this.$nextTick(() => {
        const panel = this.$el?.querySelector('.keyboard-shortcuts__panel');
        if (panel) panel.focus();
      });
    },
    close() {
      this.isOpen = false;
    },
  },
};
</script>

<style lang="scss">
@import '../styles/variables.scss';

.keyboard-shortcuts {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2001;
  display: flex;
  align-items: center;
  justify-content: center;
}

.keyboard-shortcuts__overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
}

.keyboard-shortcuts__panel {
  position: relative;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  max-width: 700px;
  max-height: 80vh;
  width: 90%;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .app--dark & {
    background: #2c2c2c;
    color: rgba(255, 255, 255, 0.9);
  }

  .app--sepia & {
    background: #f4ecd8;
    color: #5b4636;
  }

  .app--nord & {
    background: #3b4252;
    color: #d8dee9;
  }

  .app--solarized-light & {
    background: #fdf6e3;
    color: #657b83;
  }

  .app--solarized-dark & {
    background: #073642;
    color: #93a1a1;
  }

  .app--dracula & {
    background: #21222c;
    color: #f8f8f2;
  }

  .app--monokai & {
    background: #1e1f1c;
    color: #f8f8f2;
  }
}

.keyboard-shortcuts__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);

  .app--dark &,
  .app--nord &,
  .app--solarized-dark &,
  .app--dracula &,
  .app--monokai & {
    border-bottom-color: rgba(255, 255, 255, 0.1);
  }
}

.keyboard-shortcuts__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.keyboard-shortcuts__close {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: rgba(0, 0, 0, 0.8);
  }

  .app--dark &,
  .app--nord &,
  .app--solarized-dark &,
  .app--dracula &,
  .app--monokai & {
    color: rgba(255, 255, 255, 0.5);

    &:hover {
      color: rgba(255, 255, 255, 0.8);
    }
  }

  .icon {
    width: 20px;
    height: 20px;
  }
}

.keyboard-shortcuts__content {
  padding: 16px 20px;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.shortcuts-section__title {
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(0, 0, 0, 0.5);

  .app--dark &,
  .app--nord &,
  .app--solarized-dark &,
  .app--dracula &,
  .app--monokai & {
    color: rgba(255, 255, 255, 0.5);
  }
}

.shortcut-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  gap: 12px;
}

.shortcut-keys {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;

  kbd {
    display: inline-block;
    padding: 4px 8px;
    font-family: $font-family-main;
    font-size: 12px;
    font-weight: 500;
    background: rgba(0, 0, 0, 0.08);
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    min-width: 24px;
    text-align: center;
    color: rgba(0, 0, 0, 0.8);

    .app--dark & {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 255, 255, 0.25);
      color: rgba(255, 255, 255, 0.9);
    }

    .app--sepia & {
      background: rgba(91, 70, 54, 0.1);
      border-color: rgba(91, 70, 54, 0.2);
      color: #5b4636;
    }

    .app--nord & {
      background: rgba(216, 222, 233, 0.15);
      border-color: rgba(216, 222, 233, 0.25);
      color: #eceff4;
    }

    .app--solarized-light & {
      background: rgba(101, 123, 131, 0.1);
      border-color: rgba(101, 123, 131, 0.2);
      color: #586e75;
    }

    .app--solarized-dark & {
      background: rgba(147, 161, 161, 0.15);
      border-color: rgba(147, 161, 161, 0.25);
      color: #eee8d5;
    }

    .app--dracula & {
      background: rgba(248, 248, 242, 0.15);
      border-color: rgba(248, 248, 242, 0.25);
      color: #f8f8f2;
    }

    .app--monokai & {
      background: rgba(248, 248, 242, 0.15);
      border-color: rgba(248, 248, 242, 0.25);
      color: #f8f8f2;
    }
  }
}

.shortcut-desc {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.7);

  .app--dark &,
  .app--nord &,
  .app--solarized-dark &,
  .app--dracula &,
  .app--monokai & {
    color: rgba(255, 255, 255, 0.8);
  }

  .app--sepia & {
    color: rgba(91, 70, 54, 0.8);
  }

  .app--solarized-light & {
    color: rgba(101, 123, 131, 0.9);
  }
}
</style>
